import { useTranslation } from 'react-i18next'
import { Button, Grid, Stack, TextField } from '@mui/material'
import { CatalogProductInput, Generator } from '../types'
import { LoadingButton } from '@mui/lab'
import { updateGenerator } from '../services/generator'
import { useDispatch } from 'react-redux'
import { createSuccessNotification } from '../redux/slices/notifications'
import { CatalogProductStatus, CatalogProductType } from '../enums'
import { FormikHelpers, getIn, useFormik } from 'formik'
import { createCatalogProduct, updateCatalogProduct } from '../services/catalogProductService'
import * as Yup from 'yup'
import { isRequired, mustBeLessThanCharacters, mustBeString } from '../utils/validation'

export interface GeneratorDeviceCatalogProductFormProps {
  generator: Generator
  onCancel?: () => void
  onError?: (error: unknown) => void
  afterSubmit: (generator: Generator) => void
}

export function GeneratorDeviceCatalogProductForm({
  generator,
  onCancel,
  onError,
  afterSubmit
}: GeneratorDeviceCatalogProductFormProps) {
  const { t } = useTranslation('generator')

  const dispatch = useDispatch()

  const initialValues: CatalogProductInput = {
    type: CatalogProductType.GENERATOR,
    name: generator?.catalogProduct?.name ?? '',
    manufacturer: generator?.catalogProduct?.manufacturer ?? '',
    manufacturerProductCode: generator?.catalogProduct?.manufacturerProductCode ?? null,
    status: generator?.catalogProduct?.status ?? CatalogProductStatus.UNVERIFIED,
    customData: {
      apparentPower: generator.apparentPower,
      nominalVoltage: generator.nominalVoltage,
      nominalCurrent: generator.nominalCurrent ?? undefined,
      powerSource: generator.powerSource
    }
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .typeError(mustBeString(t('catalogProduct:name')))
      .required(isRequired(t('catalogProduct:name'))),

    manufacturer: Yup.string()
      .typeError(mustBeString(t('catalogProduct:manufacturer')))
      .max(255, mustBeLessThanCharacters(t('catalogProduct:manufacturer'), 255))
      .optional()
      .nullable(),

    manufacturerProductCode: Yup.string()
      .typeError(mustBeString(t('catalogProduct:manufacturerProductCode')))
      .max(255, mustBeLessThanCharacters(t('catalogProduct:manufacturerProductCode'), 255))
      .optional()
      .nullable()
  })

  const { values, touched, errors, isSubmitting, handleBlur, handleChange, handleSubmit } =
    useFormik({
      initialValues,
      validationSchema,
      onSubmit
    })

  const isError = (field: string) => {
    const fieldTouched = getIn(touched, field)
    const fieldError = getIn(errors, field)

    return Boolean(fieldTouched && fieldError)
  }

  const getHelperText = (field: string) => {
    const fieldTouched = getIn(touched, field)
    const fieldError = getIn(errors, field)

    return fieldTouched && fieldError ? fieldError : undefined
  }

  async function onSubmit(
    values: CatalogProductInput,
    { setSubmitting }: FormikHelpers<CatalogProductInput>
  ) {
    try {
      setSubmitting(true)

      const catalogProduct = generator?.catalogProduct?.id
        ? await updateCatalogProduct(generator.catalogProduct.id, values)
        : await createCatalogProduct(values)

      const updatedGenerator = await updateGenerator(generator.id, {
        catalogProduct: catalogProduct.id
      })

      dispatch(createSuccessNotification(t('catalogProductCreateSuccess')))

      afterSubmit?.(updatedGenerator)
    } catch (error) {
      // TODO: Handle server-side validation errors.
      onError?.(error)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            name="name"
            value={values.name}
            label={t('catalogProduct:name')}
            error={isError('name')}
            helperText={getHelperText('name')}
            disabled={isSubmitting}
            fullWidth
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="manufacturer"
            value={values.manufacturer}
            label={t('catalogProduct:manufacturer')}
            error={isError('manufacturer')}
            helperText={getHelperText('manufacturer')}
            disabled={isSubmitting}
            fullWidth
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="manufacturerProductCode"
            value={values.manufacturerProductCode}
            label={t('catalogProduct:manufacturerProductCode')}
            error={isError('manufacturerProductCode')}
            helperText={getHelperText('manufacturerProductCode')}
            disabled={isSubmitting}
            fullWidth
            onChange={handleChange}
            onBlur={handleBlur}
          />

          {/*<Accordion sx={{ boxShadow: 0, width: '100%' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              {t('additionalSettings')}
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={6}>
                <Grid item xs={12}>
                  <TextField
                    type="number"
                    name="customData.apparentPower"
                    value={values.customData.apparentPower}
                    label={`${t('apparentPower')} (kVA)`}
                    error={isError('customData.apparentPower')}
                    helperText={getHelperText('customData.apparentPower')}
                    disabled={isSubmitting}
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid item xs={12}>
                  <GeneratorNominalVoltageSelect
                    name="customData.nominalVoltage"
                    value={values.customData.nominalVoltage}
                    label={`${t('nominalVoltage')} (V)`}
                    error={isError('customData.nominalVoltage')}
                    helperText={getHelperText('customData.nominalVoltage')}
                    disabled={isSubmitting}
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid item xs={12}>
                  <GeneratorNominalCurrentAutocomplete
                    name="customData.nominalCurrent"
                    value={values.customData.nominalCurrent}
                    label={`${t('nominalCurrent')} (A)`}
                    error={isError('customData.nominalCurrent')}
                    helperText={getHelperText('customData.nominalCurrent')}
                    disabled={isSubmitting}
                    fullWidth
                    onChange={(_, value) => {
                      setFieldValue('customData.nominalCurrent', value)
                      setFieldTouched('customData.nominalCurrent', true)
                    }}
                    onBlur={handleBlur}
                  />
                </Grid>

                <Grid item xs={12}>
                  <GeneratorPowerSourceSelect
                    name="customData.powerSource"
                    value={values.customData.powerSource}
                    label={t('powerSource')}
                    error={isError('customData.powerSource')}
                    helperText={getHelperText('customData.powerSource')}
                    disabled={isSubmitting}
                    fullWidth
                    onChange={handleChange}
                    onBlur={handleBlur}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>*/}
        </Grid>

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <Button variant="text" color="inherit" disabled={isSubmitting} onClick={onCancel}>
              {t('actions:cancel')}
            </Button>

            <LoadingButton type="submit" variant="contained" color="primary" loading={isSubmitting}>
              {t('actions:save')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
